import { Sparkles, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type {
  TimelineEventView,
  TimelineSummaryError,
  TimelineSummaryPreview,
  TimelineSummaryRequestState,
} from "@/features/timeline";
import type { SubscriptionTier } from "@/types/projects";

type TimelineSummaryPanelProps = {
  event: TimelineEventView;
  onApproveSummary: () => void;
  onDismissPreview: () => void;
  onGenerateSummary: () => void;
  summaryError: TimelineSummaryError | null;
  summaryPreview: TimelineSummaryPreview | null;
  summaryRequestState: TimelineSummaryRequestState;
  tier: SubscriptionTier;
};

export function TimelineSummaryPanel({
  event,
  onApproveSummary,
  onDismissPreview,
  onGenerateSummary,
  summaryError,
  summaryPreview,
  summaryRequestState,
  tier,
}: TimelineSummaryPanelProps) {
  const { t } = useTranslation();
  const isPreviewing = summaryRequestState === "previewing";
  const isApproving = summaryRequestState === "approving";
  const activePreview = summaryPreview?.eventId === event.id ? summaryPreview : null;
  const summaryErrorKey = summaryError?.code === "tier-locked"
    ? "timeline.summary.errors.tierLocked"
    : summaryError?.code === "insufficient-credits"
    ? "timeline.summary.errors.insufficientCredits"
    : summaryError?.code === "access-denied"
    ? "timeline.summary.errors.accessDenied"
    : summaryError?.code === "not-found"
    ? "timeline.summary.errors.notFound"
    : summaryError?.code === "stale"
    ? "timeline.summary.errors.stale"
    : summaryError?.code === "token-invalid"
    ? "timeline.summary.errors.tokenInvalid"
    : summaryError
    ? "timeline.summary.errors.failed"
    : null;

  return (
    <section className="space-y-4 rounded-[1.35rem] bg-[#f2e7d6]/72 px-4 py-4">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {t("timeline.summary.eyebrow")}
        </p>
        <h3 className="text-xl font-semibold text-foreground">{t("timeline.summary.title")}</h3>
      </div>

      {summaryErrorKey ? (
        <div className="rounded-[20px] border border-destructive/35 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <div className="flex items-start gap-2">
            <TriangleAlert className="mt-0.5 h-4 w-4" />
            <div className="space-y-3">
              <p>{t(summaryErrorKey)}</p>
              {summaryError?.code === "tier-locked" || summaryError?.code === "insufficient-credits" ? (
                <Button asChild size="sm" type="button" variant="outline">
                  <Link to="/account/billing">
                    {summaryError.code === "tier-locked"
                      ? t("timeline.summary.upgradeAction")
                      : t("timeline.summary.topUpAction")}
                  </Link>
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {tier === "free" ? (
        <div className="rounded-[22px] border border-dashed border-amber-300/70 bg-amber-50/90 px-4 py-4 text-sm text-amber-950">
          <p className="font-semibold">{t("timeline.summary.lockedTitle")}</p>
          <p className="mt-1">{t("timeline.summary.lockedBody")}</p>
          <Button asChild className="mt-3" size="sm" type="button" variant="outline">
            <Link to="/account/billing">{t("timeline.summary.upgradeAction")}</Link>
          </Button>
        </div>
      ) : activePreview ? (
        <div className="space-y-4">
          <div className="rounded-[22px] border border-primary/20 bg-primary/5 px-4 py-4">
            <p className="text-sm font-semibold text-foreground">{activePreview.summary}</p>
          </div>
          <div className="rounded-[22px] border border-white/70 bg-secondary/35 px-4 py-4 text-sm">
            <p className="font-semibold text-foreground">{t("timeline.summary.citationTitle")}</p>
            <p className="mt-2 text-muted-foreground">
              {t("timeline.summary.citationLabel")}: {activePreview.citation.title}
            </p>
            <p className="mt-2 text-muted-foreground">{activePreview.citation.description}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button disabled={isApproving} onClick={onApproveSummary} type="button">
              {isApproving
                ? t("timeline.summary.approving")
                : t("timeline.summary.approveAction")}
            </Button>
            <Button disabled={isApproving} onClick={onDismissPreview} type="button" variant="outline">
              {t("timeline.summary.dismissAction")}
            </Button>
          </div>
        </div>
      ) : event.aiSummary ? (
        <div className="space-y-3">
          <div className="rounded-[22px] border border-emerald-300/40 bg-emerald-50 px-4 py-4">
            <p className="text-sm font-semibold text-emerald-950">{event.aiSummary}</p>
          </div>
          <p className="text-xs text-muted-foreground">
            {event.aiSummaryGeneratedAt
              ? t("timeline.summary.cachedAt", { date: event.aiSummaryGeneratedAt })
              : t("timeline.summary.cachedBody")}
          </p>
          <Button disabled={isPreviewing} onClick={onGenerateSummary} type="button" variant="outline">
            <Sparkles className="h-4 w-4" />
            {t("timeline.summary.regenerateAction")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("timeline.summary.emptyBody")}</p>
          <Button disabled={isPreviewing} onClick={onGenerateSummary} type="button">
            <Sparkles className="h-4 w-4" />
            {isPreviewing
              ? t("timeline.summary.generating")
              : t("timeline.summary.generateAction")}
          </Button>
        </div>
      )}
    </section>
  );
}
